import React, { useState, useEffect, useCallback } from 'react';
import {
  getAdminAuditLogs, getAdminAuditStats, exportAdminAuditJson,
  getAdminUsersActivity, getProjects, safeErrorMessage
} from '../services/api';
import { useToast } from '../App';
import Pagination from '../components/Pagination';
import {
  HiOutlineDocumentText, HiOutlineDownload, HiOutlineFilter,
  HiOutlineRefresh, HiOutlineUserGroup, HiOutlineClock,
} from 'react-icons/hi';
import { FiFileText, FiDollarSign, FiTrendingUp, FiShield } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid, AreaChart, Area } from 'recharts';

const ACTION_LABELS = {
  create: 'Creation',
  update: 'Modification',
  delete: 'Suppression',
  update_statut: 'Changement statut',
  update_commentaire: 'Commentaire',
  ai_create: 'Generation IA',
  ai_improve: 'Amelioration IA',
  import: 'Import',
};

const ACTION_COLORS = {
  create: '#22c55e',
  update: '#3b82f6',
  delete: '#ef4444',
  update_statut: '#f59e0b',
  ai_create: '#8b5cf6',
  ai_improve: '#a78bfa',
  import: '#06b6d4',
};

const PIE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

export default function AuditPage() {
  const addToast = useToast();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [usersActivity, setUsersActivity] = useState([]);
  const [projects, setProjects] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    project_id: '',
    user_id: '',
    entity_type: '',
    action: '',
    date_from: '',
    date_to: '',
  });
  const [statsDateFrom, setStatsDateFrom] = useState('');
  const [statsDateTo, setStatsDateTo] = useState('');
  const [activeTab, setActiveTab] = useState('logs'); // logs, stats, users, costs
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    date_from: '', date_to: '', project_id: '',
    include_audit: true, include_ai: true, include_users: true,
  });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await getAdminAuditLogs(params);
      setLogs(data.items);
      setTotal(data.total);
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Erreur chargement audit logs'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, addToast]);

  const statsParams = useCallback(() => {
    const p = {};
    if (statsDateFrom) p.date_from = statsDateFrom;
    if (statsDateTo) p.date_to = statsDateTo;
    return p;
  }, [statsDateFrom, statsDateTo]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await getAdminAuditStats(statsParams());
      setStats(data);
    } catch { /* ignore */ }
  }, [statsParams]);

  const loadUsersActivity = useCallback(async () => {
    try {
      const { data } = await getAdminUsersActivity(statsParams());
      setUsersActivity(data);
    } catch { /* ignore */ }
  }, [statsParams]);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await getProjects();
      setProjects(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => {
    loadStats();
    loadUsersActivity();
  }, [loadStats, loadUsersActivity]);

  const openExportModal = () => {
    setExportOptions({
      date_from: statsDateFrom || filters.date_from || '',
      date_to: statsDateTo || filters.date_to || '',
      project_id: filters.project_id || '',
      include_audit: true, include_ai: true, include_users: true,
    });
    setShowExportModal(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportOptions.date_from) params.date_from = exportOptions.date_from;
      if (exportOptions.date_to) params.date_to = exportOptions.date_to;
      if (exportOptions.project_id) params.project_id = exportOptions.project_id;
      const { data } = await exportAdminAuditJson(params);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = exportOptions.date_from || exportOptions.date_to
        ? `${exportOptions.date_from || 'debut'}_${exportOptions.date_to || 'fin'}`
        : 'global';
      a.download = `audit_export_${suffix}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      addToast('success', 'Export JSON telecharge');
      setShowExportModal(false);
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Erreur export'));
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setFilters({ project_id: '', user_id: '', entity_type: '', action: '', date_from: '', date_to: '' });
    setPage(1);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const formatChanges = (changesStr) => {
    try {
      const changes = JSON.parse(changesStr);
      if (!changes || typeof changes !== 'object') return null;
      const keys = Object.keys(changes);
      if (keys.length === 0) return null;
      return (
        <div className="audit-changes">
          {keys.slice(0, 3).map(k => (
            <div key={k} className="audit-change-item">
              <span className="audit-change-field">{k}</span>
              {changes[k]?.before !== undefined && (
                <span className="audit-change-before">{String(changes[k].before).slice(0, 40)}</span>
              )}
              {changes[k]?.after !== undefined && (
                <span className="audit-change-after">{String(changes[k].after).slice(0, 40)}</span>
              )}
            </div>
          ))}
          {keys.length > 3 && <span className="text-muted" style={{ fontSize: '0.7rem' }}>+{keys.length - 3} champs</span>}
        </div>
      );
    } catch { return null; }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h2>
          <FiFileText style={{ marginRight: 8 }} />
          Journalisation & Audit
        </h2>
        <div className="actions">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { loadLogs(); loadStats(); }} title="Rafraichir">
            <HiOutlineRefresh />
          </button>
          <button className="btn btn-success btn-sm" onClick={openExportModal}>
            <HiOutlineDownload style={{ marginRight: 4 }} />
            Export JSON
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="audit-tabs mb-3">
        {[
          { key: 'logs', label: 'Journal des actions', icon: <HiOutlineDocumentText /> },
          { key: 'stats', label: 'Statistiques', icon: <FiTrendingUp /> },
          { key: 'users', label: 'Activite Utilisateurs', icon: <HiOutlineUserGroup /> },
          { key: 'costs', label: 'Couts IA', icon: <FiDollarSign /> },
        ].map(tab => (
          <button
            key={tab.key}
            className={`audit-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Logs */}
      {activeTab === 'logs' && (
        <>
          {/* Filters */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-2">
                <HiOutlineFilter />
                <strong style={{ fontSize: '0.85rem' }}>Filtres</strong>
                <button className="btn btn-link btn-sm" onClick={resetFilters} style={{ fontSize: '0.75rem' }}>Reinitialiser</button>
              </div>
              <div className="row g-2">
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={filters.project_id}
                    onChange={e => { setFilters({ ...filters, project_id: e.target.value }); setPage(1); }}>
                    <option value="">Tous les projets</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={filters.entity_type}
                    onChange={e => { setFilters({ ...filters, entity_type: e.target.value }); setPage(1); }}>
                    <option value="">Toutes les entites</option>
                    <option value="requirement">Exigences</option>
                    <option value="test_case">Cas de test</option>
                    <option value="defect">Defauts</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={filters.action}
                    onChange={e => { setFilters({ ...filters, action: e.target.value }); setPage(1); }}>
                    <option value="">Toutes les actions</option>
                    {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <input type="date" className="form-control form-control-sm" value={filters.date_from}
                    onChange={e => { setFilters({ ...filters, date_from: e.target.value }); setPage(1); }}
                    placeholder="Date debut" />
                </div>
                <div className="col-md-2">
                  <input type="date" className="form-control form-control-sm" value={filters.date_to}
                    onChange={e => { setFilters({ ...filters, date_to: e.target.value }); setPage(1); }}
                    placeholder="Date fin" />
                </div>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
              {total} enregistrement{total > 1 ? 's' : ''} trouves
            </span>
          </div>

          {/* Table */}
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 150 }}>Date</th>
                      <th>Utilisateur</th>
                      <th>Projet</th>
                      <th>Entite</th>
                      <th>Action</th>
                      <th>Element</th>
                      <th>Modifications</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="text-center py-4">
                        <output className="spinner-border spinner-border-sm" /> Chargement...
                      </td></tr>
                    ) : logs.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-4 text-muted">Aucun log trouve</td></tr>
                    ) : logs.map(l => (
                      <tr key={l.id}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                          <HiOutlineClock style={{ marginRight: 3 }} />
                          {formatDate(l.performed_at)}
                        </td>
                        <td className="fw-500">{l.performed_by_username}</td>
                        <td>
                          {l.project_name ? (
                            <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                              {l.project_code || l.project_name}
                            </span>
                          ) : '-'}
                        </td>
                        <td>
                          <span className={`badge audit-entity-badge audit-entity-${l.entity_type}`}>
                            {l.entity_type === 'requirement' ? 'Exigence' :
                              l.entity_type === 'test_case' ? 'Cas de test' :
                              l.entity_type === 'defect' ? 'Defaut' : l.entity_type}
                          </span>
                        </td>
                        <td>
                          <span className="audit-action-badge" style={{ backgroundColor: ACTION_COLORS[l.action] || '#6b7a90', color: '#fff' }}>
                            {ACTION_LABELS[l.action] || l.action}
                          </span>
                        </td>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.entity_label || '-'}
                        </td>
                        <td>{formatChanges(l.changes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {total > pageSize && (
            <div className="mt-3">
              <Pagination currentPage={page} totalItems={total} pageSize={pageSize} onPageChange={setPage} />
            </div>
          )}
        </>
      )}

      {/* Tab: Stats */}
      {activeTab === 'stats' && !stats && (
        <div className="text-center py-4">
          <output className="spinner-border spinner-border-sm" /> Chargement des statistiques...
        </div>
      )}
      {activeTab === 'stats' && stats && (
        <div>
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <label className="form-label mb-0" style={{ fontSize: '0.82rem' }}>Periode :</label>
            <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
              value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)} />
            <span style={{ fontSize: '0.82rem' }}>a</span>
            <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
              value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)} />
            {(statsDateFrom || statsDateTo) && (
              <button className="btn btn-outline-secondary btn-sm" onClick={() => { setStatsDateFrom(''); setStatsDateTo(''); }}
                title="Reinitialiser la periode">Tout</button>
            )}
          </div>

          {/* KPI Cards */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value">{stats.total_actions}</div>
                  <div className="audit-stat-label">Actions totales</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value">{stats.top_users?.length || 0}</div>
                  <div className="audit-stat-label">Utilisateurs actifs</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value">{stats.top_projects?.length || 0}</div>
                  <div className="audit-stat-label">Projets actifs</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value" style={{ color: 'var(--green-600)' }}>
                    ${stats.ai_costs?.total_cost_usd?.toFixed(2) || '0.00'}
                  </div>
                  <div className="audit-stat-label">Cout IA total (USD)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Chart: Activite journaliere par type */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Activite journaliere par type d'operation</h6>
                <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                  {(stats.daily_activity || []).length} jour(s) d'activite
                </span>
              </div>
              {(stats.daily_activity || []).length === 0 ? (
                <div className="text-center text-muted py-4" style={{ fontSize: '0.82rem' }}>Aucune activite sur cette periode</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.daily_activity || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      tickFormatter={d => {
                        const parts = d.split('-');
                        return parts.length === 3 ? `${parts[2]}/${parts[1]}` : d;
                      }}
                    />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} label={{ value: 'Nombre', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888' } }} />
                    <Tooltip
                      labelFormatter={d => {
                        try { return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
                        catch { return d; }
                      }}
                      formatter={(v, name) => [v, ACTION_LABELS[name] || name]}
                      contentStyle={{ fontSize: '0.78rem' }}
                    />
                    <Legend formatter={v => ACTION_LABELS[v] || v} wrapperStyle={{ fontSize: '0.75rem' }} />
                    {Object.keys(ACTION_COLORS).map(action => (
                      <Bar key={action} dataKey={action} stackId="a" fill={ACTION_COLORS[action]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Tendance + Repartition */}
          <div className="row g-3 mb-3">
            <div className="col-md-7">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="mb-3">Tendance d'activite (total/jour)</h6>
                  {(stats.daily_activity_total || []).length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: '0.82rem' }}>Aucune donnee</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={stats.daily_activity_total || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => { const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : d; }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip
                          labelFormatter={d => { try { return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return d; } }}
                          formatter={(v) => [v, 'Operations']}
                          contentStyle={{ fontSize: '0.78rem' }}
                        />
                        <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={2} name="Operations" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="mb-3">Repartition des operations</h6>
                  {Object.keys(stats.actions_by_type || {}).length === 0 ? (
                    <div className="text-center text-muted py-5" style={{ fontSize: '0.82rem' }}>Aucune donnee sur cette periode</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={Object.entries(stats.actions_by_type || {}).map(([k, v]) => ({ name: ACTION_LABELS[k] || k, value: v }))}
                            dataKey="value" nameKey="name"
                            cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                          >
                            {Object.entries(stats.actions_by_type || {}).map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v, name) => [v + ' operations', name]} contentStyle={{ fontSize: '0.78rem' }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-1">
                        {Object.entries(stats.actions_by_type || {}).sort((a, b) => b[1] - a[1]).map(([k, v], i) => (
                          <div key={k} className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '0.78rem' }}>
                            <span>
                              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: PIE_COLORS[i % PIE_COLORS.length], marginRight: 6 }} />
                              {ACTION_LABELS[k] || k}
                            </span>
                            <strong>{v}</strong>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Top Users & Projects */}
          <div className="row g-3">
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="mb-3">Utilisateurs les plus actifs</h6>
                  {(stats.top_users || []).length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: '0.82rem' }}>Aucun utilisateur actif</div>
                  ) : (
                    <div>
                      {(stats.top_users || []).map((u, i) => {
                        const maxCount = stats.top_users[0]?.count || 1;
                        const pct = Math.round((u.count / maxCount) * 100);
                        return (
                          <div key={i} className="mb-2">
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                              <span className="fw-500">{u.username}</span>
                              <span style={{ color: 'var(--gray-500)' }}>{u.count} operations</span>
                            </div>
                            <div className="progress" style={{ height: 6, borderRadius: 3 }}>
                              <div className="progress-bar" role="progressbar"
                                style={{ width: `${pct}%`, backgroundColor: '#22c55e', borderRadius: 3 }}
                                aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="mb-3">Projets les plus actifs</h6>
                  {(stats.top_projects || []).length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: '0.82rem' }}>Aucun projet actif</div>
                  ) : (
                    <div>
                      {(stats.top_projects || []).map((p, i) => {
                        const maxCount = stats.top_projects[0]?.count || 1;
                        const pct = Math.round((p.count / maxCount) * 100);
                        return (
                          <div key={i} className="mb-2">
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                              <span className="fw-500">{p.project_name}</span>
                              <span style={{ color: 'var(--gray-500)' }}>{p.count} operations</span>
                            </div>
                            <div className="progress" style={{ height: 6, borderRadius: 3 }}>
                              <div className="progress-bar" role="progressbar"
                                style={{ width: `${pct}%`, backgroundColor: '#3b82f6', borderRadius: 3 }}
                                aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Users Activity */}
      {activeTab === 'users' && usersActivity.length === 0 && (
        <div className="text-center py-4">
          <output className="spinner-border spinner-border-sm" /> Chargement...
        </div>
      )}
      {activeTab === 'users' && usersActivity.length > 0 && (
        <div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <FiShield />
            <strong style={{ fontSize: '0.85rem' }}>Supervision des utilisateurs</strong>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
              {statsDateFrom || statsDateTo
                ? `(${statsDateFrom || '...'} — ${statsDateTo || '...'})`
                : '(90 derniers jours)'}
            </span>
          </div>
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th>Utilisateur</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Statut</th>
                      <th>Actions</th>
                      <th>Derniere action</th>
                      <th>Inscription</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersActivity.map(u => (
                      <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                        <td className="fw-500">{u.full_name || u.username}</td>
                        <td style={{ color: 'var(--gray-500)', fontSize: '0.78rem' }}>{u.email}</td>
                        <td>
                          <span className={`user-role-badge user-role-${u.role}`}>
                            {{ superadmin: 'Super admin', admin: 'Admin' }[u.role] || 'Utilisateur'}
                          </span>
                        </td>
                        <td>
                          <span className={`user-status-badge ${u.is_active ? 'user-status-active' : 'user-status-inactive'}`}>
                            {u.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td>
                          <strong style={{ color: u.action_count > 0 ? 'var(--green-600)' : 'var(--gray-400)' }}>
                            {u.action_count}
                          </strong>
                        </td>
                        <td style={{ fontSize: '0.75rem' }}>{u.last_action_at ? formatDate(u.last_action_at) : 'Jamais'}</td>
                        <td style={{ fontSize: '0.75rem' }}>{formatDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: AI Costs */}
      {activeTab === 'costs' && !stats && (
        <div className="text-center py-4">
          <output className="spinner-border spinner-border-sm" /> Chargement...
        </div>
      )}
      {activeTab === 'costs' && stats && (
        <div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <FiDollarSign />
            <strong style={{ fontSize: '0.85rem' }}>Consommation IA — Rapport financier</strong>
            <input type="date" className="form-control form-control-sm ms-2" style={{ width: 'auto' }}
              value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)} />
            <span style={{ fontSize: '0.82rem' }}>a</span>
            <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
              value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)} />
            {(statsDateFrom || statsDateTo) && (
              <button className="btn btn-outline-secondary btn-sm" onClick={() => { setStatsDateFrom(''); setStatsDateTo(''); }}>Tout</button>
            )}
          </div>

          {/* Cost KPIs */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value" style={{ color: '#22c55e' }}>
                    ${stats.ai_costs?.total_cost_usd?.toFixed(4) || '0.00'}
                  </div>
                  <div className="audit-stat-label">Cout total (USD)</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value">
                    {((stats.ai_costs?.total_cost_usd || 0) * 0.92).toFixed(4)}
                  </div>
                  <div className="audit-stat-label">Cout total (EUR approx.)</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value">
                    {((stats.ai_costs?.by_project || []).reduce((s, p) => s + (p.tokens_in + p.tokens_out), 0)).toLocaleString()}
                  </div>
                  <div className="audit-stat-label">Tokens totaux</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value">
                    {(stats.ai_costs?.by_project || []).reduce((s, p) => s + p.calls, 0)}
                  </div>
                  <div className="audit-stat-label">Appels IA</div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost by provider */}
          <div className="row g-3">
            <div className="col-md-6">
              <div className="card">
                <div className="card-body">
                  <h6 className="mb-3">Couts par fournisseur / modele</h6>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0" style={{ fontSize: '0.82rem' }}>
                      <thead>
                        <tr>
                          <th>Fournisseur</th>
                          <th>Modele</th>
                          <th className="text-end">Cout (USD)</th>
                          <th className="text-end">Tokens</th>
                          <th className="text-end">Appels</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stats.ai_costs?.by_provider || []).map((r, i) => (
                          <tr key={i}>
                            <td className="fw-500">{r.provider}</td>
                            <td style={{ fontSize: '0.75rem' }}>{r.model}</td>
                            <td className="text-end" style={{ color: '#22c55e' }}>${r.total_cost_usd.toFixed(4)}</td>
                            <td className="text-end">{r.total_tokens.toLocaleString()}</td>
                            <td className="text-end">{r.calls}</td>
                          </tr>
                        ))}
                        {(stats.ai_costs?.by_provider || []).length === 0 && (
                          <tr><td colSpan={5} className="text-center text-muted">Aucune donnee</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card">
                <div className="card-body">
                  <h6 className="mb-3">Couts par projet</h6>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0" style={{ fontSize: '0.82rem' }}>
                      <thead>
                        <tr>
                          <th>Projet</th>
                          <th className="text-end">Cout (USD)</th>
                          <th className="text-end">Tokens IN</th>
                          <th className="text-end">Tokens OUT</th>
                          <th className="text-end">Appels</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stats.ai_costs?.by_project || []).map((r, i) => (
                          <tr key={i}>
                            <td className="fw-500">{r.project_name}</td>
                            <td className="text-end" style={{ color: '#22c55e' }}>${r.total_cost_usd.toFixed(4)}</td>
                            <td className="text-end">{r.tokens_in.toLocaleString()}</td>
                            <td className="text-end">{r.tokens_out.toLocaleString()}</td>
                            <td className="text-end">{r.calls}</td>
                          </tr>
                        ))}
                        {(stats.ai_costs?.by_project || []).length === 0 && (
                          <tr><td colSpan={5} className="text-center text-muted">Aucune donnee</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ fontSize: '1rem' }}>
                  <HiOutlineDownload style={{ marginRight: 6 }} />
                  Export JSON — Audit
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowExportModal(false)} aria-label="Fermer" />
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
                  Configurez les parametres de l'export JSON pour les auditeurs.
                  Par defaut, l'export contient toutes les donnees disponibles (export global).
                </p>

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 500 }}>Periode</label>
                  <div className="d-flex align-items-center gap-2">
                    <input type="date" className="form-control form-control-sm"
                      value={exportOptions.date_from}
                      onChange={e => setExportOptions({ ...exportOptions, date_from: e.target.value })} />
                    <span style={{ fontSize: '0.82rem' }}>a</span>
                    <input type="date" className="form-control form-control-sm"
                      value={exportOptions.date_to}
                      onChange={e => setExportOptions({ ...exportOptions, date_to: e.target.value })} />
                  </div>
                  <div className="form-text" style={{ fontSize: '0.72rem' }}>
                    Laisser vide pour exporter toutes les donnees
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 500 }}>Projet</label>
                  <select className="form-select form-select-sm"
                    value={exportOptions.project_id}
                    onChange={e => setExportOptions({ ...exportOptions, project_id: e.target.value })}>
                    <option value="">Tous les projets (global)</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                <div className="mb-2">
                  <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 500 }}>Contenu de l'export</label>
                  <div className="d-flex flex-column gap-1">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="exp-audit" checked={exportOptions.include_audit}
                        onChange={e => setExportOptions({ ...exportOptions, include_audit: e.target.checked })} />
                      <label className="form-check-label" htmlFor="exp-audit" style={{ fontSize: '0.82rem' }}>
                        Journal des actions (audit trail)
                      </label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="exp-ai" checked={exportOptions.include_ai}
                        onChange={e => setExportOptions({ ...exportOptions, include_ai: e.target.checked })} />
                      <label className="form-check-label" htmlFor="exp-ai" style={{ fontSize: '0.82rem' }}>
                        Consommation IA (couts, tokens)
                      </label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="exp-users" checked={exportOptions.include_users}
                        onChange={e => setExportOptions({ ...exportOptions, include_users: e.target.checked })} />
                      <label className="form-check-label" htmlFor="exp-users" style={{ fontSize: '0.82rem' }}>
                        Activite des utilisateurs
                      </label>
                    </div>
                  </div>
                </div>

                <div className="alert alert-light mt-3 mb-0" style={{ fontSize: '0.75rem', border: '1px solid var(--gray-200)' }}>
                  <strong>Format :</strong> JSON structure — compatible avec les outils d'audit bancaire.
                  {!exportOptions.date_from && !exportOptions.date_to && !exportOptions.project_id && (
                    <span className="d-block mt-1 text-primary fw-500">Export global (toutes les donnees)</span>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowExportModal(false)}>
                  Annuler
                </button>
                <button type="button" className="btn btn-success btn-sm" onClick={handleExport} disabled={exporting}>
                  <HiOutlineDownload style={{ marginRight: 4 }} />
                  {exporting ? 'Telechargement...' : 'Telecharger JSON'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
